/**
 * @date: 2022-04-12
 * @author: Kevin Hsiau <khsiau@max.md>
 * @copy: (C) Copyright 2022 MaxMD Corporation, All Rights Reserved.
 */

import React, {useEffect, useState} from "react";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    styled,
    TableCell,
    TableContainer,
    Tooltip,
    Typography
} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import {convertDateToTimeString} from "../utils/cert-helper";
import InfoIcon from '@mui/icons-material/Info';
import {useCookies} from "react-cookie";
import {getComparator, sortedRowInformation} from "../Hisps/HispsAsList";

const StyledTableRow = styled(TableRow)(({theme}) => ({
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));
const StyledTableRowHeader = styled(TableRow)({
    '& .MuiTableCell-head': {
        fontWeight: 'bold'
    },
})
const StyledInfoDiv = styled('div') ({
    display: 'flex',
    gap: '5px',
    alignItems: 'center'
})
const StyledTypography = styled(Typography)({
    fontSize: '0.875rem',
})
const errorTooltipMsg = `Untolerated + tolerated Error`

export default function PublishedErrorTable(props) {
    const contributions = props.contributions || [];
    const hispsLogo = props.hispsLogo;

    const [sortedContributions, setSortedContributions] = useState([]);
    const [options, setOptions] = useState(0);

    // cookie to store options
    const [cookie, setCookie] = useCookies(['errorOption'])

    useEffect(() => {
        const sorted = sortedRowInformation(contributions, getComparator('asc', 'errorCount'));
        if (sorted.length === 0) {
            console.error(`Contribution not found`)
            return;
        }
        if (cookie.errorOption) {
            if (cookie.errorOption > 0) {
                setOptions(cookie.errorOption);
                setSortedContributions(sorted.slice(0, cookie.errorOption))
            } else {
                setOptions(0);
                setSortedContributions(sorted.slice());
            }
        } else {
            setSortedContributions(sorted.slice());
        }
    }, [options])

    const handleCookie = (o) => {
        let expires = new Date();
        expires.setMonth(expires.getMonth() + 1);
        setCookie('errorOption', o, { path: '/', expires});
    }


    const handleSelectChange = (event) => {
        const { value } = event.target;
        setOptions(value);
        handleCookie(value);
    }

    return (
        <TableContainer sx={{mt: '10px'}}>
            <FormControl sx={{ mt: '20px', mb: '20px', width: '200px'}}>
                <InputLabel id='select-label'>Options</InputLabel>
                <Select
                    labelId='select-label'
                    id='select-options'
                    value={options}
                    label='Options'
                    onChange={handleSelectChange}
                    size='small'
                >
                    <MenuItem value={0}>Show All</MenuItem>
                    <MenuItem value={3}>Top 3</MenuItem>
                    <MenuItem value={5}>Top 5</MenuItem>
                    <MenuItem value={10}>Top 10</MenuItem>
                </Select>
            </FormControl>
            <Table sx={{m: 0}}>
                <TableHead>
                    <StyledTableRowHeader>
                        <TableCell sx={{ width: 100}}>No.</TableCell>
                        <TableCell sx={{ width: 150}}> </TableCell>
                        <TableCell>HISP</TableCell>
                        <TableCell>
                            <StyledInfoDiv>
                                Total Error Count
                                <Tooltip title={errorTooltipMsg} arrow placement='top'>
                                    <InfoIcon fontSize='small'/>
                                </Tooltip>
                            </StyledInfoDiv>
                        </TableCell>
                        <TableCell>Tolerated Errors</TableCell>
                        <TableCell>Untolerated Errors</TableCell>
                        <TableCell>Submission</TableCell>
                    </StyledTableRowHeader>
                </TableHead>
                <TableBody>
                    {sortedContributions && sortedContributions.map((c, index) => {
                            let logo = "";
                            if (hispsLogo.hasOwnProperty(c.hisp)) {
                                logo = hispsLogo[c.hisp]
                            }

                            return (
                                <StyledTableRow key={c.id}>
                                    <TableCell>
                                        {index + 1}
                                    </TableCell>
                                    <TableCell>
                                        {logo && <img src={logo} alt={c.hisp} style={{ maxWidth: 75, maxHeight: 50, marginTop: '8px'}} />}
                                    </TableCell>
                                    <TableCell>
                                        <StyledTypography fontWeight='bold'>{c.hisp}</StyledTypography>
                                    </TableCell>
                                    <TableCell>{c.errorCount}<br/></TableCell>
                                    <TableCell>{c.toleratedErrorCount}</TableCell>
                                    <TableCell>{c.errorCount - c.toleratedErrorCount}</TableCell>
                                    <TableCell>
                                        <>
                                            Submission Time<br/>
                                            <span style={{color: '#1769aa'}}>{convertDateToTimeString(c.submissionTs)}</span>
                                        </>
                                    </TableCell>
                                </StyledTableRow>
                            )
                    }

                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
